import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Mail, MessageCircle, Send, Phone, Video, Calendar,
  Plus, Search, Filter, Users, Settings, Paperclip,
  Check, CheckCheck, Circle, Smile, MoreVertical
} from 'lucide-react';
import { useToast } from '../components/shared/ToastProvider';

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const Communication = () => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('messages');
  const [messageThreads, setMessageThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [showNewThreadModal, setShowNewThreadModal] = useState(false);
  const [showEmailSetupModal, setShowEmailSetupModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  // Mock current user data
  const currentUser = {
    id: 'user123',
    name: 'John Recruiter',
    tenant_id: 'tenant1'
  };

  useEffect(() => {
    // Check for session_id in URL fragment (Emergent OAuth callback)
    const handleEmergentCallback = async () => {
      const fragment = window.location.hash;
      if (fragment.includes('session_id=')) {
        const sessionId = fragment.split('session_id=')[1].split('&')[0];
        const urlParams = new URLSearchParams(window.location.search);
        const tenantId = urlParams.get('tenant_id');
        const userId = urlParams.get('user_id');
        
        if (sessionId && tenantId && userId) {
          try {
            setLoading(true);
            const response = await axios.post(`${API}/api/communication/email/auth/emergent/session`, {
              session_id: sessionId,
              tenant_id: tenantId,
              user_id: userId
            });
            
            if (response.data.success) {
              // Store session token in localStorage or cookie
              localStorage.setItem('emergent_session_token', response.data.session_token);
              showToast(`Gmail connected successfully for ${response.data.user.name}`);
              
              // Clean URL fragment and query params
              window.history.replaceState({}, document.title, window.location.pathname);
              
              // Refresh email accounts
              fetchEmailAccounts();
            }
          } catch (error) {
            console.error('Error processing Emergent session:', error);
            showToast('Failed to connect Gmail account', 'error');
          } finally {
            setLoading(false);
          }
        }
      }
    };

    // Check existing session on component mount
    const checkExistingSession = async () => {
      const sessionToken = localStorage.getItem('emergent_session_token');
      if (sessionToken) {
        try {
          const response = await axios.get(`${API}/api/communication/email/auth/session?session_token=${sessionToken}`);
          if (response.data.authenticated) {
            console.log('User authenticated:', response.data.user);
          } else {
            localStorage.removeItem('emergent_session_token');
          }
        } catch (error) {
          console.error('Error validating session:', error);
          localStorage.removeItem('emergent_session_token');
        }
      }
    };

    handleEmergentCallback();
    checkExistingSession();
    fetchMessageThreads();
    fetchEmailAccounts();
    fetchEmailTemplates();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const connectWebSocket = () => {
    try {
      const wsUrl = `ws://localhost:8001/ws/${currentUser.tenant_id}/${currentUser.id}`;
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
      };
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
      };
      
      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected');
        // Reconnect after 3 seconds
        setTimeout(connectWebSocket, 3000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Error connecting WebSocket:', error);
    }
  };

  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'new_message':
        if (selectedThread && data.message.thread_id === selectedThread.id) {
          setMessages(prev => [...prev, data.message]);
        }
        // Update thread list
        fetchMessageThreads();
        break;
      case 'typing_indicator':
        // Handle typing indicators
        break;
      case 'presence_update':
        // Update online users
        break;
      default:
        break;
    }
  };

  const fetchMessageThreads = async () => {
    try {
      const response = await axios.get(`${API}/api/communication/messages/threads/${currentUser.tenant_id}?user_id=${currentUser.id}`);
      setMessageThreads(response.data.threads || []);
    } catch (error) {
      console.error('Error fetching message threads:', error);
      // Mock data for development
      setMessageThreads([
        {
          id: '1',
          thread_name: 'Hiring Team Discussion',
          thread_type: 'group',
          participants: [
            { user_id: 'user1', name: 'Sarah Johnson', role: 'hiring_manager' },
            { user_id: 'user2', name: 'Mike Chen', role: 'recruiter' }
          ],
          last_message_at: new Date().toISOString(),
          related_job_id: 'job123'
        },
        {
          id: '2',
          thread_name: 'John Doe - Software Engineer',
          thread_type: 'candidate',
          participants: [
            { user_id: 'candidate1', name: 'John Doe', role: 'candidate' }
          ],
          last_message_at: new Date(Date.now() - 3600000).toISOString(),
          related_candidate_id: 'candidate123'
        }
      ]);
    }
  };

  const fetchEmailAccounts = async () => {
    try {
      // Check for connected email accounts in database
      const sessionToken = localStorage.getItem('emergent_session_token');
      if (sessionToken) {
        // In a real implementation, we'd fetch from an endpoint that returns user's email accounts
        // For now, we'll check if session is valid and show connected status
        const response = await axios.get(`${API}/api/communication/email/auth/session?session_token=${sessionToken}`);
        if (response.data.authenticated) {
          setEmailAccounts([{
            id: '1',
            email: response.data.user.email,
            provider: 'emergent_gmail',
            name: response.data.user.name,
            connected: true
          }]);
        } else {
          setEmailAccounts([]);
        }
      } else {
        setEmailAccounts([]);
      }
    } catch (error) {
      console.error('Error fetching email accounts:', error);
      setEmailAccounts([]);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/api/communication/email/templates?tenant_id=${currentUser.tenant_id}`);
      setEmailTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching email templates:', error);
      setEmailTemplates([]);
    }
  };

  const fetchThreadMessages = async (threadId) => {
    try {
      const response = await axios.get(`${API}/api/communication/messages/${threadId}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching thread messages:', error);
      // Mock messages for development
      setMessages([
        {
          id: '1',
          sender_id: 'user1',
          sender_name: 'Sarah Johnson',
          message_text: 'Hi team, we need to discuss the Software Engineer position candidates.',
          sent_at: new Date(Date.now() - 7200000).toISOString(),
          is_read: true
        },
        {
          id: '2',
          sender_id: currentUser.id,
          sender_name: currentUser.name,
          message_text: 'Sure! I have 3 strong candidates to review. When would be a good time?',
          sent_at: new Date(Date.now() - 3600000).toISOString(),
          is_read: true
        },
        {
          id: '3',
          sender_id: 'user1',
          sender_name: 'Sarah Johnson',
          message_text: 'How about tomorrow at 2 PM? We can go through each candidate profile.',
          sent_at: new Date(Date.now() - 1800000).toISOString(),
          is_read: false
        }
      ]);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedThread) return;

    try {
      const messageData = {
        tenant_id: currentUser.tenant_id,
        sender_id: currentUser.id,
        sender_name: currentUser.name,
        message_text: newMessage,
        message_type: 'text'
      };

      const response = await axios.post(
        `${API}/api/communication/messages/${selectedThread.id}`,
        messageData
      );

      if (response.data.success) {
        setNewMessage('');
        // Message will be added via WebSocket
      }
    } catch (error) {
      console.error('Error sending message:', error);
      showToast('Failed to send message', 'error');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const connectEmailAccount = async (provider) => {
    try {
      if (provider === 'gmail') {
        // Use Emergent OAuth system for Gmail
        const redirectUrl = `${window.location.origin}/communication`;
        const response = await axios.get(
          `${API}/api/communication/email/auth/${provider}?tenant_id=${currentUser.tenant_id}&user_id=${currentUser.id}&redirect_url=${encodeURIComponent(redirectUrl)}`
        );
        
        if (response.data.auth_url) {
          // Redirect to Emergent OAuth (not popup)
          window.location.href = response.data.auth_url;
        }
      } else {
        // Use existing OAuth flow for other providers
        const response = await axios.get(
          `${API}/api/communication/email/auth/${provider}?tenant_id=${currentUser.tenant_id}&user_id=${currentUser.id}&redirect_url=${encodeURIComponent(window.location.origin)}`
        );
        
        if (response.data.auth_url) {
          window.open(response.data.auth_url, '_blank', 'width=600,height=600');
        }
      }
    } catch (error) {
      console.error('Error connecting email account:', error);
      showToast('Failed to connect email account', 'error');
    }
  };

  const disconnectEmailAccount = async (accountId) => {
    try {
      const sessionToken = localStorage.getItem('emergent_session_token');
      if (sessionToken) {
        await axios.post(`${API}/api/communication/email/auth/logout`, {
          session_token: sessionToken
        });
        
        localStorage.removeItem('emergent_session_token');
        setEmailAccounts([]);
        showToast('Email account disconnected successfully');
      }
    } catch (error) {
      console.error('Error disconnecting email account:', error);
      showToast('Failed to disconnect email account', 'error');
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <MessageCircle className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Communication Center</h1>
              <p className="text-gray-600">Messages, emails, and team collaboration</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowNewThreadModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              <span>New Conversation</span>
            </button>
            
            <button
              onClick={() => setShowEmailSetupModal(true)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Mail className="h-4 w-4" />
              <span>Connect Email</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl shadow-md mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'messages', label: 'Messages', icon: MessageCircle },
              { id: 'email', label: 'Email', icon: Mail },
              { id: 'templates', label: 'Templates', icon: Settings }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  showToast(`Switched to ${tab.label} tab`, 'info');
                }}
                className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-96">
              {/* Thread List */}
              <div className="border border-gray-200 rounded-lg">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Conversations</h3>
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                
                <div className="overflow-y-auto max-h-80">
                  {messageThreads.map((thread) => (
                    <div
                      key={thread.id}
                      onClick={() => {
                        setSelectedThread(thread);
                        fetchThreadMessages(thread.id);
                        showToast(`Loading conversation: ${thread.thread_name}`, 'info');
                      }}
                      className={`p-4 cursor-pointer hover:bg-gray-50 border-b border-gray-100 ${
                        selectedThread?.id === thread.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-gray-900 text-sm truncate">
                          {thread.thread_name}
                        </h4>
                        <span className="text-xs text-gray-500">
                          {new Date(thread.last_message_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mb-2">
                        {thread.participants?.map(p => p.name).join(', ')}
                      </p>
                      {thread.thread_type === 'candidate' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                          Candidate
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Chat Area */}
              <div className="lg:col-span-2 border border-gray-200 rounded-lg flex flex-col">
                {selectedThread ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{selectedThread.thread_name}</h3>
                        <p className="text-sm text-gray-600">
                          {selectedThread.participants?.map(p => p.name).join(', ')}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Phone className="h-5 w-5 text-gray-400 cursor-pointer hover:text-gray-600" />
                        <Video className="h-5 w-5 text-gray-400 cursor-pointer hover:text-gray-600" />
                        <MoreVertical className="h-5 w-5 text-gray-400 cursor-pointer hover:text-gray-600" />
                      </div>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${
                            message.sender_id === currentUser.id ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-3 py-2 rounded-lg ${
                              message.sender_id === currentUser.id
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-900'
                            }`}
                          >
                            {message.sender_id !== currentUser.id && (
                              <p className="text-xs font-medium mb-1 opacity-75">
                                {message.sender_name}
                              </p>
                            )}
                            <p className="text-sm">{message.message_text}</p>
                            <div className="flex items-center justify-end mt-1 space-x-1">
                              <span className="text-xs opacity-75">
                                {new Date(message.sent_at).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                              {message.sender_id === currentUser.id && (
                                message.is_read ? <CheckCheck className="h-3 w-3" /> : <Check className="h-3 w-3" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Message Input */}
                    <div className="p-4 border-t border-gray-200">
                      <div className="flex items-center space-x-2">
                        <Paperclip className="h-5 w-5 text-gray-400 cursor-pointer hover:text-gray-600" />
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                          placeholder="Type a message..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <Smile className="h-5 w-5 text-gray-400 cursor-pointer hover:text-gray-600" />
                        <button
                          onClick={sendMessage}
                          disabled={!newMessage.trim()}
                          className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Send className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">Select a conversation to start messaging</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Email Tab */}
        {activeTab === 'email' && (
          <div className="p-6">
            <div className="text-center py-12">
              <Mail className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Integration</h3>
              <p className="text-gray-600 mb-6">Connect your email accounts to manage communications</p>
              
  {emailAccounts.length === 0 ? (
                <div className="space-y-3">
                  <button
                    onClick={() => connectEmailAccount('gmail')}
                    disabled={loading}
                    className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 mr-3 disabled:opacity-50"
                  >
                    {loading ? 'Connecting...' : 'Connect Gmail'}
                  </button>
                  <button
                    onClick={() => connectEmailAccount('outlook')}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Connect Outlook
                  </button>
                </div>
              ) : (
                <div className="max-w-md mx-auto">
                  <h4 className="text-lg font-semibold mb-4">Connected Email Accounts</h4>
                  {emailAccounts.map((account) => (
                    <div key={account.id} className="bg-gray-50 rounded-lg p-4 mb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center text-white font-semibold">
                            {account.provider === 'emergent_gmail' ? 'G' : 'O'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{account.name}</p>
                            <p className="text-sm text-gray-600">{account.email}</p>
                            <p className="text-xs text-gray-500">
                              {account.provider === 'emergent_gmail' ? 'Gmail (via Emergent)' : 'Outlook'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            Connected
                          </span>
                          <button
                            onClick={() => disconnectEmailAccount(account.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Disconnect
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <button
                    onClick={() => connectEmailAccount('outlook')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                  >
                    + Add Another Account
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Templates</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {emailTemplates.map((template) => (
                  <div key={template.id} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">{template.name}</h4>
                    <p className="text-sm text-gray-600 mb-3">{template.category}</p>
                    <p className="text-xs text-gray-500 mb-4">
                      Subject: {template.subject_template}
                    </p>
                    <div className="flex space-x-2">
                      <button className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200">
                        Edit
                      </button>
                      <button className="px-3 py-1 text-xs bg-gray-100 text-gray-800 rounded hover:bg-gray-200">
                        Use
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Communication;